import { useState, useRef, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import api, { getErrorMessage } from '@/services/api'

interface ContactFormData {
  name: string
  email: string
  phone: string
  subject: string
  category: string
  message: string
  recaptchaToken?: string
}

type FormErrors = Partial<Record<keyof ContactFormData, string>>

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MOBILE_RE = /^(\+44|44|0)7\d{9}$/

function validateForm(data: ContactFormData): FormErrors {
  const errors: FormErrors = {}
  if (!data.name.trim()) errors.name = 'Full name is required.'
  if (!data.email.trim()) {
    errors.email = 'Email address is required.'
  } else if (!EMAIL_RE.test(data.email.trim())) {
    errors.email = 'Please enter a valid email address.'
  }
  if (data.phone.trim()) {
    const cleaned = data.phone.replace(/[\s\-\(\)]/g, '')
    if (!MOBILE_RE.test(cleaned)) errors.phone = 'Please enter a valid UK mobile number (e.g. 07700 900000).'
  }
  if (!data.subject.trim()) errors.subject = 'Subject is required.'
  if (!data.message.trim()) errors.message = 'Message is required.'
  else if (data.message.trim().length < 20) errors.message = 'Message must be 20 characters or more.'
  return errors
}

export default function Contact() {
  const [searchParams] = useSearchParams()
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    subject: searchParams.get('subject') || '',
    category: 'general',
    message: '',
  })
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({})
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    const sub = searchParams.get('subject')
    if (sub) setFormData(f => ({ ...f, subject: sub }))
  }, [searchParams])
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const submitMutation = useMutation({
    mutationFn: async (data: ContactFormData) => {
      const response = await api.post('/tickets/public', data)
      return response.data
    },
    onSuccess: () => {
      setSubmitted(true)
      setFormData({ name: '', email: '', phone: '', subject: '', category: 'general', message: '' })
      setFieldErrors({})
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const errors = validateForm(formData)
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    setFieldErrors({})
    submitMutation.mutate({ ...formData, recaptchaToken: recaptchaToken || undefined })
  }

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto py-8 sm:py-12 px-4">
        <Card className="p-6 sm:p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2">Message Sent!</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6">
            Thank you for contacting us. Someone will be in touch soon.
          </p>
          <Button variant="primary" onClick={() => setSubmitted(false)}>
            Send Another Message
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8">Contact Us</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        <div className="lg:col-span-2">
          <Card className="p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Send us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-4">
                <div>
                  <Input
                    label="Full Name"
                    value={formData.name}
                    onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setFieldErrors((f) => ({ ...f, name: undefined })) }}
                  />
                  {fieldErrors.name && <p className="mt-1 text-sm text-red-600">{fieldErrors.name}</p>}
                </div>
                <div>
                  <Input
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setFieldErrors((f) => ({ ...f, email: undefined })) }}
                  />
                  {fieldErrors.email && <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-4">
                <div>
                  <Input
                    label="Phone Number (optional)"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => { setFormData({ ...formData, phone: e.target.value }); setFieldErrors((f) => ({ ...f, phone: undefined })) }}
                    placeholder="e.g. 07700 900000"
                  />
                  {fieldErrors.phone && <p className="mt-1 text-sm text-red-600">{fieldErrors.phone}</p>}
                </div>
                <Select
                  label="Category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  options={[
                    { value: 'general', label: 'General Inquiry' },
                    { value: 'sales', label: 'Sales' },
                    { value: 'support', label: 'Technical Support' },
                    { value: 'billing', label: 'Billing' },
                    { value: 'complaints', label: 'Complaints' },
                  ]}
                />
              </div>
              <div>
                <Input
                  label="Subject"
                  value={formData.subject}
                  onChange={(e) => { setFormData({ ...formData, subject: e.target.value }); setFieldErrors((f) => ({ ...f, subject: undefined })) }}
                />
                {fieldErrors.subject && <p className="mt-1 text-sm text-red-600">{fieldErrors.subject}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-silux-primary ${fieldErrors.message ? 'border-red-500' : 'border-gray-300'}`}
                  rows={6}
                  value={formData.message}
                  onChange={(e) => { setFormData({ ...formData, message: e.target.value }); setFieldErrors((f) => ({ ...f, message: undefined })) }}
                />
                {fieldErrors.message && <p className="mt-1 text-sm text-red-600">{fieldErrors.message}</p>}
              </div>

              {submitMutation.error && (
                <Alert variant="error">
                  {getErrorMessage(submitMutation.error)}
                </Alert>
              )}

              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                  onChange={setRecaptchaToken}
                  onExpired={() => setRecaptchaToken(null)}
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={submitMutation.isPending || !recaptchaToken}
              >
                {submitMutation.isPending ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
            <div className="space-y-4">
              <div>
                <p className="font-medium">Customer Service</p>
                <p className="text-silux-primary">01246 912899</p>
              </div>
              <div>
                <p className="font-medium">Email</p>
                <p className="text-silux-primary">info@siluxmobile.co.uk</p>
              </div>
              <div>
                <p className="font-medium">Sales</p>
                <p className="text-silux-primary">sales@siluxmobile.co.uk</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Office Address</h3>
            <p className="text-gray-600">
              Silux Mobile<br />
              13 Prospect House<br />
              Colliery Close, Staveley<br />
              Chesterfield<br />
              S43 3QE<br />
              United Kingdom
            </p>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Business Hours</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Monday - Friday</span>
                <span>9:00 AM - 5:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span>Saturday &amp; Sunday</span>
                <span>Closed</span>
              </div>
              <div className="flex justify-between">
                <span>Bank holidays</span>
                <span>Closed</span>
              </div>
              <div className="flex justify-between">
                <span>Chat support</span>
                <span>24 hours</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
